import React, { useEffect } from 'react';
import { useRouter } from 'next/router';
import PageSeo from '../../components/Common/PageSeo';
import { useTranslations } from '../../locales/useTranslations';
import Navbar from '../../components/Layouts/Navbar';
import Footer from '../../components/Layouts/Footer';
import BlogDetail from '../../components/Blog/BlogDetail';
import BlogNotFound from '../../components/Blog/BlogNotFound';
import {
    fetchBlogPosts,
    resolveBlogPostBySlug,
    getBlogAlternateSlug,
} from '../../lib/blogApi';

const BlogPost = ({ post, alternateSlug }) => {
    const { t } = useTranslations();
    const router = useRouter();
    const locale = router.locale || 'de';
    const baseUrl = locale === 'en' ? 'https://simplify-data.com' : 'https://simplify-data.de';
    const deSlug = locale === 'de' ? post?.slug : (alternateSlug || post?.slug);
    const enSlug = locale === 'en' ? post?.slug : (alternateSlug || post?.slug);

    // PageView tracking (client-seitig)
    useEffect(() => {
        if (post?.id && typeof window !== 'undefined') {
            const trackPageView = async () => {
                try {
                    const apiUrl = process.env.NEXT_PUBLIC_API_URL || 'https://api.simplify-data.de';
                    await fetch(`${apiUrl}/api/pageview`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            collection: 'blog',
                            item_id: String(post.id)
                        })
                    });
                } catch (error) {
                    console.debug('PageView tracking failed:', error);
                }
            };
            trackPageView();
        }
    }, [post?.id]);

    // 404 wenn kein Post gefunden
    if (!post) {
        return (
            <>
                <Navbar />
                <BlogNotFound />
                <Footer />
            </>
        );
    }

    return (
        <>
            <PageSeo
                title={`${post.title} | Simplify Data Blog`}
                description={post.excerpt}
                canonical={`${baseUrl}/blog/${post.slug}/`}
                openGraph={{
                    url: `${baseUrl}/blog/${post.slug}/`,
                    title: post.title,
                    description: post.excerpt,
                    type: 'article',
                    article: {
                        publishedTime: post.dateISO,
                        authors: [post.author],
                        tags: [post.category],
                    },
                    images: [
                        {
                            url: post.image,
                            width: 1200,
                            height: 630,
                            alt: post.title,
                            type: 'image/jpeg',
                        },
                    ],
                    siteName: 'Simplify Data Blog',
                }}
                twitter={{
                    handle: '@simplify_data',
                    site: '@simplify_data',
                    cardType: 'summary_large_image',
                }}
                additionalMetaTags={[
                    {
                        name: 'robots',
                        content: 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1',
                    },
                    {
                        name: 'author',
                        content: post.author,
                    },
                    {
                        name: 'geo.region',
                        content: 'DE-SH',
                    },
                    {
                        name: 'geo.placename',
                        content: 'Kiel',
                    },
                    {
                        property: 'article:modified_time',
                        content: new Date().toISOString(),
                    },
                ]}
                additionalLinkTags={[
                    {
                        rel: 'icon',
                        href: `${baseUrl}/images/favicon.png`,
                        type: 'image/png'
                    },
                    {
                        rel: 'alternate',
                        hrefLang: 'de',
                        href: `https://simplify-data.de/blog/${deSlug}/`
                    },
                    {
                        rel: 'alternate',
                        hrefLang: 'en',
                        href: `https://simplify-data.com/blog/${enSlug}/`
                    },
                    {
                        rel: 'alternate',
                        hrefLang: 'x-default',
                        href: `https://simplify-data.de/blog/${deSlug}/`
                    }
                ]}
            />

            <Navbar />
            <BlogDetail post={post} />
            <Footer />
        </>
    );
};

export async function getStaticPaths({ locales }) {
    const paths = [];
    for (const locale of locales || ['de', 'en']) {
        try {
            const posts = await fetchBlogPosts(locale);
            for (const post of posts) {
                paths.push({ params: { slug: post.slug }, locale });
            }
        } catch {
            // bei Fehler keine Pfade für diese Locale
        }
    }
    return {
        paths,
        fallback: 'blocking',
    };
}

export async function getStaticProps({ params, locale }) {
    const slug = params?.slug;
    if (!slug) return { notFound: true };

    try {
        const { post, redirectSlug } = await resolveBlogPostBySlug(slug, locale || 'de');

        // Falscher Locale-Slug (z. B. EN-Slug auf .de) → korrekte URL
        if (redirectSlug) {
            return {
                redirect: {
                    destination: `/blog/${redirectSlug}/`,
                    permanent: true,
                },
            };
        }

        if (!post) return { notFound: true };

        const alternateSlug = await getBlogAlternateSlug(post.id, locale || 'de');

        return {
            props: {
                post,
                alternateSlug: alternateSlug || post.slug,
            },
            revalidate: 300,
        };
    } catch {
        return { notFound: true };
    }
}

export default BlogPost;
